import { useState, useMemo, useEffect } from "react";
import { useAuth } from "wasp/client/auth";
import {
  getAccountsByUser,
  getAccountGroupsByUser,
  getAccountChargesByUser,
  getTradingStrategiesByUser,
  getTptWalletContext,
  useQuery,
} from "wasp/client/operations";
import { startOfMonth } from "date-fns";
import { Button } from "../client/components/ui/button";
import { PageTransition } from "../client/components/ui/motion";
import { usePfmData } from "./PfmDataContext";
import PfmLayoutShell from "./PfmLayoutShell";
import PlanSelectionModal from "../dashboard/PlanSelectionModal";
import PropFirmImportModal from "./PropFirmImportModal";
import AddPayoutModal from "./AddPayoutModal";
import useTranslation from "../client/hooks/useTranslation";
import { PaymentPlanId, SubscriptionStatus } from "../payment/plans";
import { useIsDark, useThemeId } from "../client/components/ui/card-section-header";
import { WidgetDataProvider, type DashboardData } from "../dashboard/widgets/WidgetDataContext";
import { presetToDateRange } from "../client/utils/dateRange";
import type { DateRange } from "../client/components/DateRangePicker";
import type { AccountFilterValue } from "../trading-accounts/AccountGroupFilter";
import type { CalendarEvent } from "./PropFirmCalendar";
import { CURRENCY } from "../shared/currency";
import { buildAccountCalendarEvents } from "./accountCalendarEvents";
import { chargeCalendarInstances } from "./chargeSchedule";

function PfmInner({ isFreePlan }: { isFreePlan: boolean }) {
  const t = useTranslation();
  const {
    accounts,
    hiddenAccountsCount,
    showImportModal, setShowImportModal,
    showAddPayoutModal, setShowAddPayoutModal,
    showPlanModal, setShowPlanModal,
    isLoadingPfm: isLoading,
    userId,
  } = usePfmData();

  useEffect(() => {
    if (!userId) return;
    const timer = setTimeout(() => {
      localStorage.setItem(`onboarding_propFirmManagerVisited_${userId}`, "true");
    }, 2500);
    return () => clearTimeout(timer);
  }, [userId]);

  if (isLoading) {
    return (
      <PageTransition className="w-full py-10">
        <p className="text-sm text-muted-foreground">{t.common.loading}</p>
      </PageTransition>
    );
  }

  return (
    <PageTransition className="w-full pt-6 pb-10 space-y-4">
      {hiddenAccountsCount > 0 && (
        <div className="flex items-center justify-between rounded-lg border border-yellow-500/30 bg-yellow-500/10 px-4 py-3 text-sm text-yellow-300">
          <span>{t.tradingAccounts.hiddenBanner.title.replace("{{count}}", String(hiddenAccountsCount))}</span>
          <Button
            variant="outline"
            size="sm"
            className="ml-4 shrink-0 border-yellow-500/40 text-yellow-300 hover:bg-yellow-500/20"
            onClick={() => setShowPlanModal(true)}
          >
            {t.tradingAccounts.hiddenBanner.cta}
          </Button>
        </div>
      )}

      <PfmLayoutShell isFreePlan={isFreePlan} />

      <PlanSelectionModal open={showPlanModal} onOpenChange={setShowPlanModal} />
      <PropFirmImportModal open={showImportModal} onOpenChange={setShowImportModal} />
      <AddPayoutModal
        open={showAddPayoutModal}
        onClose={() => setShowAddPayoutModal(false)}
        accounts={(accounts ?? []) as { id: string; name: string; status: string }[]}
        onSuccess={() => {}}
      />
    </PageTransition>
  );
}

function PfmDataBridge() {
  const t = useTranslation();
  const isDark = useIsDark();
  const themeId = useThemeId();
  const { data: authUser } = useAuth();

  const isFreePlan = useMemo(() => {
    if (!authUser) return false;
    if (!authUser.subscriptionPlan || authUser.subscriptionPlan === PaymentPlanId.Free) return true;
    if (!authUser.subscriptionStatus || authUser.subscriptionStatus === SubscriptionStatus.Deleted) return true;
    return false;
  }, [authUser]);

  // State
  const [pfmTemplateFilter, setPfmTemplateFilter] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<DateRange>(() => presetToDateRange("last_30_days"));
  const [calendarMonth, setCalendarMonth] = useState(() => startOfMonth(new Date()));
  const [showImportModal, setShowImportModal] = useState(false);
  const [showAddPayoutModal, setShowAddPayoutModal] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [accountFilter, setAccountFilter] = useState<AccountFilterValue>({ type: "all" });
  const [strategyIds, setStrategyIds] = useState<string[]>([]);

  // Queries
  const { data: accountsData, isLoading: isLoadingAccounts } = useQuery(getAccountsByUser);
  const { data: groupsData } = useQuery(getAccountGroupsByUser);
  const { data: chargesData, isLoading: isLoadingCharges } = useQuery(getAccountChargesByUser);
  const { data: strategiesData = [] } = useQuery(getTradingStrategiesByUser);
  const { data: tptWalletContext } = useQuery(getTptWalletContext);

  const accounts = ((accountsData as any)?.accounts ?? []) as any[];
  const hiddenAccountsCount: number = (accountsData as any)?.hiddenAccountsCount ?? 0;
  const groups = (groupsData ?? []) as any[];
  const charges = (chargesData ?? []) as any[];
  const strategies = (strategiesData ?? []) as any[];
  const isLoadingPfm = isLoadingAccounts || isLoadingCharges;

  // Derived: filteredAccounts (filter by pfmTemplateFilter)
  const filteredAccounts = useMemo(() => {
    let all = [...accounts];
    if (pfmTemplateFilter) {
      all = all.filter((a: any) => a.propFirmTemplateId === pfmTemplateFilter);
    }
    return all;
  }, [accounts, pfmTemplateFilter]);

  // Derived: calendarEvents
  const calendarEvents = useMemo<CalendarEvent[]>(() => {
    const events: CalendarEvent[] = [];
    const now = new Date();

    for (const acc of filteredAccounts) {
      events.push(...buildAccountCalendarEvents(acc, CURRENCY));
    }

    const chargeList = charges as {
      id: string;
      amount: number;
      frequency: string;
      accountId?: string | null;
      name: string;
      createdAt: Date | string;
      startDate?: Date | string | null;
      endDate?: Date | string | null;
      billingDay?: number | null;
    }[];

    for (const charge of chargeList) {
      if (charge.accountId) {
        const belongs = filteredAccounts.some((a: any) => a.id === charge.accountId);
        if (!belongs) continue;
      }
      // Même règle que les KPIs (chargeSchedule) : un abonnement fermé ne
      // projette plus aucun prélèvement après sa date de fin.
      const eventType = charge.frequency === "one_time" ? "expense" : "recurring";
      for (const { date, amount } of chargeCalendarInstances(charge, now)) {
        events.push({ date, type: eventType, amount, currency: CURRENCY, label: charge.name });
      }
    }

    return events;
  }, [filteredAccounts, charges]);

  const widgetData: DashboardData = {
    // Trading data — not available on PFM page, use sensible defaults
    trades: [],
    routines: [],
    goals: [],
    sessions: [],
    metrics: null,
    statsTrades: [],
    statsTradesList: [],
    equityCurveData: { points: [], totalPnl: 0, xMin: 0, xMax: 0 },
    accountEvents: [],
    fundedEvents: [],
    payoutEvents: [],
    violationsByDate: new Map(),
    pendingRoutines: [],
    topGoals: [],
    statsPeriod: "30d",
    statsStartDate: null,
    statsEndDate: null,
    setSelectedDay: () => {},
    handleCompleteRoutine: () => {},
    setSelectedMonth: () => {},
    onAddTrade: () => {},
    onPromoteToFunded: () => {},
    onOpenPlanModal: () => {},
    isDark,
    themeId,
    isFreePlan,
    readOnly: false,
    t,
    chartMode: "pnl",
    setChartMode: () => {},

    // Filter state
    accountFilter,
    setAccountFilter,
    strategyIds,
    setStrategyIds,
    hasMentor: false,

    // PFM-specific real data
    accounts,
    groups,
    strategies,
    charges,
    tptWalletContext,
    hiddenAccountsCount,
    filteredAccounts,
    calendarEvents,
    pfmTemplateFilter,
    setPfmTemplateFilter,
    dateRange,
    setDateRange,
    calendarMonth,
    setCalendarMonth,
    showImportModal,
    setShowImportModal,
    showAddPayoutModal,
    setShowAddPayoutModal,
    showPlanModal,
    setShowPlanModal,
    isLoadingPfm,
    userId: authUser?.id,
  } as any;

  return (
    <WidgetDataProvider value={widgetData}>
      <PfmInner isFreePlan={isFreePlan} />
    </WidgetDataProvider>
  );
}

export default function PfmView() {
  return <PfmDataBridge />;
}
